import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, DataTable, LoadingSpinner, Tabs, AvatarStack, AppsStack, useDataTableUrl } from '@airclou/ui';
import { Building2, Plus, LayoutGrid } from 'lucide-react';
import { getCompany, type Company } from '@/api/companies';
import { isRecordGone } from '@/api/errors';
import { listWorkspaces, type Workspace } from '@/api/workspaces';
import { CreateWorkspaceModal } from '../../components/hierarchy/CreateWorkspaceModal';
import { CompanyMembersTab } from '../../components/hierarchy/CompanyMembersTab';
import { CompanyGroupsTab } from '../../components/hierarchy/CompanyGroupsTab';
import { CompanySettingsTab } from '../../components/hierarchy/CompanySettingsTab';
import { ErrorState } from '../../components/state/ErrorState';
import { RefreshErrorBanner } from '../../components/state/RefreshErrorBanner';
import { useTimezone } from '@airclou/shell/timezone'
import { formatDateInTz } from '@airclou/utils'
import { useAuth } from '../../contexts/AuthContext'
import { canManageOrg } from '../../navigation'
import { RoleUnknownNotice } from '../../components/state/RoleUnknownNotice'

export function CompanyDetailsPage() {
  const timezone = useTimezone()
  const { t } = useTranslation('dashboard');
  // `POST /api/workspaces` is `MANAGE` as of 2026-09-13 (role plan §6.4).
  // Listing the company's workspaces stays open to any member.
  const { user } = useAuth();
  const canManage = canManageOrg(user?.orgRole ?? undefined);
  const { id, tab } = useParams<{ id: string; tab?: string }>();
  const navigate = useNavigate();
  const [company, setCompany] = useState<Company | null>(null);
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // The company is missing or not ours: a retry cannot help, so the page has
  // nothing left to show even if it is still holding rows from an earlier load.
  // Cleared only when a load succeeds, so retrying from the error state does
  // not flash the deleted company — and its editable form — back on screen.
  const [gone, setGone] = useState(false);
  const [isCreateWorkspaceModalOpen, setIsCreateWorkspaceModalOpen] = useState(false);
  // The Workspaces tab's sort lives in the URL (same pattern as the audit log).
  // Tabs are separate paths, so switching tabs drops it.
  const { sorting, onSortingChange } = useDataTableUrl();

  const activeTab = tab || 'overview';

  // Which load is the current one. A company switch starts a new load without
  // remounting the page, so a slow answer for the company we left can still be
  // in flight — and writing it would put that company's name, workspaces and
  // settings form under the new company's URL, where a save would post its
  // edits to the id in the URL.
  const loadSeq = useRef(0);
  // Written from the id-change effect, not during render: a render React starts
  // and abandons must not publish an id that never mounted. Every caller of
  // `loadData` runs after a commit.
  const shownId = useRef(id);

  // Both have to agree: `CompanySettingsTab`'s `onUpdate` reload starts after a
  // company switch, so it would otherwise take the newest sequence number and
  // beat the answer for the company now in the URL.
  const isCurrent = (seq: number, loadId?: string) =>
    seq === loadSeq.current && loadId === shownId.current;

  const loadData = async () => {
    if (!id) return;

    // Bail before claiming a sequence number. A stale caller — `onUpdate` from a
    // save that resolved after a company switch — would otherwise take the
    // newest sequence, then disqualify itself on the id check, orphaning the
    // live load: neither answer is "current", so nothing writes and nothing
    // clears `loading`, leaving the page on skeletons with no error and no retry.
    if (id !== shownId.current) return;

    const seq = ++loadSeq.current;
    const loadId = id;
    try {
      setLoading(true);
      // Cleared on success rather than up front: the retry affordance lives
      // inside the banner and the error state, and clearing here unmounts it the
      // instant it is clicked, so the retry reads as a dead button.
      const [companyResponse, workspacesResponse] = await Promise.all([
        getCompany(id),
        listWorkspaces(id),
      ]);
      if (!isCurrent(seq, loadId)) return;
      setCompany(companyResponse.company);
      setWorkspaces(workspacesResponse.workspaces);
      setGone(false);
      setError(null);
    } catch (err: any) {
      if (!isCurrent(seq, loadId)) return;
      // Latches: only a successful load clears it. Assigning the result instead
      // would let a transient failure on the retry clear it while the stale row
      // is still in state, putting a deleted company's editable settings form
      // back on screen, where a save PATCHes a dead id.
      if (isRecordGone(err)) setGone(true);
      setError(err.message || t('companies.failedToLoadDetails'));
    } finally {
      // Only the current load owns the flag; a superseded one clearing it would
      // report the load that replaced it as finished.
      if (isCurrent(seq, loadId)) setLoading(false);
    }
  };

  useEffect(() => {
    shownId.current = id;
    // A different company under the same mounted route (a URL edit, a bookmark,
    // back/forward): drop the previous one's data before loading the new one.
    // Nothing from company A may render — or be saved — under company B's id,
    // and the settings form has to be torn down with it, because its dirty
    // values are deliberately kept across a reload of the same company.
    setCompany(null);
    setWorkspaces([]);
    // `gone` belongs to the company we were showing; a different id has not
    // answered yet, so leaving it set renders a company that exists as missing
    // for the whole of its first request.
    setGone(false);
    // `error` belongs to the company we were showing too, and `loadData` no
    // longer clears it up front — leaving it set would open the new id's first
    // load on the previous one's error page.
    setError(null);
    loadData();
  }, [id]);

  const workspaceColumns: any[] = [
    {
      id: 'name',
      header: t('common.name'),
      size: 250,
      isFlexible: true,
      cell: (workspace: Workspace) => (
        <div>
          <div className="font-medium text-foreground">{workspace.name}</div>
          <div className="text-sm text-gray-500 dark:text-gray-400">{workspace.slug}</div>
        </div>
      ),
    },
    {
      id: 'members',
      header: t('common.members'),
      size: 120,
      cell: (workspace: Workspace) => {
        const members = workspace.members_preview || [];
        const totalCount = workspace.user_count || members.length;
        if (members.length === 0) {
          return <span className="text-sm text-gray-400">—</span>;
        }
        const avatarUsers = members.map(m => ({
          id: m.user_id,
          name: m.name,
          avatar: m.avatar,
          email: m.email,
        }));
        // If there are more members than preview, add the remaining count
        const remainingCount = totalCount - members.length;
        if (remainingCount > 0) {
          // Add placeholder users for overflow display
          for (let i = 0; i < remainingCount; i++) {
            avatarUsers.push({ id: `overflow-${i}`, name: '', avatar: undefined, email: undefined });
          }
        }
        return <AvatarStack users={avatarUsers.slice(0, totalCount)} maxVisible={4} size="sm" />;
      },
    },
    {
      id: 'apps',
      header: t('common.applications'),
      size: 140,
      cell: (workspace: Workspace) => {
        const apps = workspace.apps_preview || [];
        if (apps.length === 0) {
          return <span className="text-sm text-gray-400">—</span>;
        }
        const appItems = apps.map(app => ({
          id: app.app_id,
          name: app.app_name,
          slug: app.app_slug,
        }));
        return <AppsStack apps={appItems} maxVisible={3} size="xs" />;
      },
    },
    {
      id: 'created_at',
      header: t('common.created'),
      size: 100,
      accessorKey: 'created_at',
      enableSorting: true,
      cell: (workspace: Workspace) => (
        <div className="text-sm text-gray-600 dark:text-gray-400">
          {formatDateInTz(workspace.created_at, timezone)}
        </div>
      ),
    },
  ];

  // Keyed off `error` as well as `!loading`, so the error state survives its
  // own retry instead of flashing the skeleton shell mid-flight.
  if (gone || (!company && (error || !loading))) {
    return (
      <div className="space-y-6">
        <ErrorState
          title={t('companies.failedToLoadDetails')}
          description={error || t('companies.companyNotFound')}
          onRetry={loadData}
          isRetrying={loading}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-4" aria-busy={!company}>
            <div className="flex items-center justify-center w-16 h-16 rounded-xl bg-primary/15 dark:bg-primary/25">
              <Building2 className="w-8 h-8 text-primary" />
            </div>
            <div>
              {company ? (
                <>
                  <h1 className="text-2xl font-bold text-foreground">{company.name}</h1>
                  <p className="mt-1 text-sm text-muted-foreground">{company.slug}</p>
                </>
              ) : (
                <>
                  <div className="h-8 w-48 animate-pulse rounded bg-muted" />
                  <div className="mt-2 h-4 w-24 animate-pulse rounded bg-muted" />
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* A refresh that failed while the company page still holds good data:
          keep the page, surface the failure with a retry rather than swapping
          it out. A "company is gone" answer took the early return above. */}
      {error && company && (
        <RefreshErrorBanner message={error} onRetry={loadData} isRetrying={loading} />
      )}

      {/* Tabs */}
      {/* Keyed by company: the tab bodies hold per-company state (fetched rows,
          an in-progress edit) that must not carry across a company switch. */}
      <Tabs
        key={id}
        tabs={[
          {
            id: 'overview',
            label: t('common.overview'),
            content: company ? (
              <CompanySettingsTab company={company} onUpdate={() => loadData()} />
            ) : (
              <div className="flex min-h-[300px] items-center justify-center">
                <LoadingSpinner />
              </div>
            ),
          },
          {
            id: 'workspaces',
            label: t('common.workspaces'),
            content: (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-semibold text-foreground">{t('common.workspaces')}</h2>
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                      {t('companies.manageWorkspaces')}
                    </p>
                  </div>
                  {canManage && (
                    <Button onClick={() => setIsCreateWorkspaceModalOpen(true)}>
                      <Plus className="w-4 h-4 mr-2" />
                      {t('workspaces.createWorkspace')}
                    </Button>
                  )}
                </div>
                <RoleUnknownNotice />

                <div className="relative">
                  {loading && workspaces.length > 0 && (
                    <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/50 dark:bg-gray-900/50">
                      <LoadingSpinner />
                    </div>
                  )}
                  {workspaces.length === 0 && !loading ? (
                    <div className="p-12 text-center rounded-lg border-2 border-dashed border-gray-200 dark:border-gray-700">
                      <LayoutGrid className="w-12 h-12 mx-auto text-gray-400 dark:text-gray-600 mb-4" />
                      <h3 className="text-lg font-medium text-foreground mb-2">
                        {t('companies.noWorkspacesYet')}
                      </h3>
                      {/* "Create workspaces to get started" under a button that
                          has been removed is an instruction the page cannot
                          honour. A non-manager gets told who can instead. */}
                      <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
                        {canManage ? t('companies.createWorkspacesHelp') : t('companies.onlyAdminsCanCreateWorkspaces')}
                      </p>
                      {canManage && (
                        <Button onClick={() => setIsCreateWorkspaceModalOpen(true)}>
                          <Plus className="w-4 h-4 mr-2" />
                          {t('workspaces.createWorkspace')}
                        </Button>
                      )}
                    </div>
                  ) : (
                    <DataTable
                      data={workspaces}
                      columns={workspaceColumns}
                      sorting={sorting}
                      onSortingChange={onSortingChange}
                      getRowHref={(workspace) => `/workspaces/${workspace.id}`}
                      isLoading={loading && workspaces.length === 0}
                    />
                  )}
                </div>
              </div>
            ),
          },
          {
            id: 'members',
            label: t('common.members'),
            content: <CompanyMembersTab companyId={id!} />,
          },
          {
            id: 'groups',
            label: t('common.groups'),
            content: <CompanyGroupsTab companyId={id!} />,
          },
        ]}
        activeTab={activeTab}
        onTabChange={(val) => navigate(`/companies/${id}/${val}`)}
      />

      <CreateWorkspaceModal
        isOpen={isCreateWorkspaceModalOpen}
        onClose={() => setIsCreateWorkspaceModalOpen(false)}
        companyId={id!}
        onSuccess={() => {
          setIsCreateWorkspaceModalOpen(false);
          loadData();
        }}
      />
    </div>
  );
}
